/**
 * Sales Invoice - the outflow sales register, costed on the margin.
 *
 * Winnie's v9 note asked for the sales AR as a table in the sidebar, sitting under the
 * Margin Scheme (GST Register), drawn from the deal pipeline in the sold status. Her 7 Sep
 * note renames the heading to "Sales Invoice"; the sidebar keeps "Sales (AR)", which is what
 * she asked for on 1 Sep, so the nav still says what kind of screen this is.
 *
 * The money on this screen is the margin-scheme view of a sale: what was charged, what
 * credit the purchase released, and the GST actually remitted on the difference. The rule
 * behind it is the one in lib/gstTreatment, shared with the GST Register, so the two
 * screens cannot drift apart.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateRange, ALL_TIME } from '../../lib/dateRange'
import { errText } from '../../lib/errText'
import ReportRangeFilter from './ReportRangeFilter'
import {
  SalesArReport,
  SalesArRow,
  ArPaymentFilter,
  AR_PAYMENT_OPTIONS,
  loadSalesAr,
  salesArCsv,
  recordPayment,
} from '../../services/salesArService'
import './SalesArView.css'
import { AR_PAYMENT_STATUS_LABEL } from '../../lib/arPayments'
import { User } from '../../types'
import { acknowledgeAmendedFlag } from '../../services/saleAmendmentService'
import { toast } from '../Toast'

const money = (n: number) =>
  '$' +
  Number(n || 0).toLocaleString('en-AU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

const fmtDate = (iso: string | null) => {
  if (!iso) return '-'
  const d = new Date(String(iso).slice(0, 10) + 'T00:00:00Z')
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

function download(name: string, body: string) {
  const url = URL.createObjectURL(new Blob([body], { type: 'text/csv;charset=utf-8;' }))
  const a = document.createElement('a')
  a.href = url
  a.download = name
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * The Paid cell. Type what the buyer has actually paid and press Enter, or Tab away.
 *
 * Winnie, 10 Sep: when the amount received matches the sale, the row gets a green tick,
 * the status becomes Settled and it drops off AR reminders. Those are one fact, so this
 * writes one field and everything else reads it.
 *
 * Nothing here can move commission. That was struck at Sold and the database refuses to
 * change it - see build spec v2 section 4.
 */
function PaidCell({ row, onSaved }: { row: SalesArRow; onSaved: () => void }) {
  const [draft, setDraft] = useState<string>(row.received === null ? '' : String(row.received))
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  useEffect(() => {
    setDraft(row.received === null ? '' : String(row.received))
  }, [row.received])

  const commit = async () => {
    const typed = draft.trim()
    const next = typed === '' ? 0 : Number(typed)
    if (!Number.isFinite(next)) {
      setProblem('Enter the amount received as a number.')
      return
    }
    if (next === (row.received ?? 0)) {
      setProblem(null)
      return
    }
    setBusy(true)
    setProblem(null)
    try {
      const res = await recordPayment(row.dealId, row.invoiceId, next, row.sale)
      if (!res.ok) {
        setProblem(res.message)
        return
      }
      onSaved()
    } catch (err) {
      setProblem(errText(err, 'Could not record that payment.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <span className="sar-paid">
      <input
        className="sar-paid-input"
        value={draft}
        disabled={busy}
        inputMode="decimal"
        aria-label={'Amount received for ' + row.vehicle}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
        }}
      />
      {problem && <span className="sar-paid-problem">{problem}</span>}
    </span>
  )
}

export default function SalesArView({ user }: { user: User }) {
  const [range, setRange] = useState<DateRange>(ALL_TIME)
  const [payment, setPayment] = useState<ArPaymentFilter>('all')
  const [query, setQuery] = useState('')
  const [report, setReport] = useState<SalesArReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [ackId, setAckId] = useState<string | null>(null)

  /**
   * Acknowledging the amended flag. Winnie: "Clearing the flag is a manual acknowledgement
   * action... an unacknowledged flag should persist until someone actually looks at it."
   */
  const acknowledge = async (invoiceId: string | null) => {
    if (!invoiceId) return
    setAckId(invoiceId)
    try {
      await acknowledgeAmendedFlag(invoiceId, user)
      toast.saved()
      await refresh()
    } catch (err) {
      toast.error(errText(err, 'The flag could not be acknowledged.'))
    } finally {
      setAckId(null)
    }
  }

  const refresh = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setReport(await loadSalesAr(range, payment, query))
    } catch (err) {
      setError(errText(err, 'The sales AR could not be loaded.'))
    } finally {
      setLoading(false)
    }
  }, [range, payment, query])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const rows = useMemo(() => report?.rows ?? [], [report])
  const totals = report?.totals

  return (
    <div className="sar">
      <div className="sar-head">
        <h1>Sales Invoice</h1>
      </div>
      <p className="sar-sub">
        Every vehicle sold through the deal pipeline, with the GST worked out on the margin.
        Net GST is the GST charged on the sale less the credit that sale releases &mdash; one
        eleventh of the margin where the car made money, and nil where it did not, because
        the notional credit cannot exceed the GST on the sale. A sale counts as
        <strong> realised</strong> once it has been invoiced; until then its GST is a
        <strong> forecast</strong>, and the Margin Scheme (GST Register) will not put it on a
        BAS. The realised total below is the figure that register reports for the same period.
      </p>

      <div className="sar-toolbar">
        <ReportRangeFilter value={range} onChange={setRange} label="Sale date" />

        <label className="sar-field">
          Invoicing
          <select
            value={payment}
            onChange={(e) => setPayment(e.target.value as ArPaymentFilter)}
          >
            {AR_PAYMENT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <label className="sar-field">
          Search
          <input
            type="text"
            placeholder="Stock, VIN, vehicle, customer"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>

        <div className="sar-spacer" />

        <button
          type="button"
          className="sar-btn"
          disabled={!rows.length}
          onClick={() => download('sales-ar-margin-scheme.csv', salesArCsv(rows))}
        >
          Export CSV
        </button>
      </div>

      {error && <div className="sar-alert sar-bad">{error}</div>}

      {totals && (
        <div className="sar-tiles">
          <div className="sar-tile">
            <span className="sar-tile-label">Vehicles sold</span>
            <strong>{totals.count}</strong>
          </div>
          <div className="sar-tile">
            <span className="sar-tile-label">Sale value (inc GST)</span>
            <strong>{money(totals.sale)}</strong>
          </div>
          <div className="sar-tile">
            <span className="sar-tile-label">Margin (inc GST)</span>
            <strong>{money(totals.marginIncGst)}</strong>
          </div>
          <div className="sar-tile">
            <span className="sar-tile-label">Net GST &mdash; realised</span>
            <strong>{money(totals.netGstRealised)}</strong>
          </div>
          <div className="sar-tile">
            <span className="sar-tile-label">Net GST &mdash; projected</span>
            <strong className="sar-projected">{money(totals.netGstProjected)}</strong>
          </div>
          <div className="sar-tile">
            <span className="sar-tile-label">Margin (ex GST)</span>
            <strong>{money(totals.marginExGst)}</strong>
          </div>
        </div>
      )}

      {loading ? (
        <div className="sar-loading">Loading the sales ledger...</div>
      ) : !rows.length ? (
        <div className="sar-none">
          No sales in {report?.periodLabel ?? 'this period'} match these filters.
        </div>
      ) : (
        <div className="sar-scroll">
          <table className="sar-table">
            <thead>
              <tr>
                <th>Sale date</th>
                <th>Stock #</th>
                <th>VIN</th>
                <th>Vehicle</th>
                <th>Customer</th>
                <th className="sar-num">Purchase</th>
                <th className="sar-num">Sale</th>
                <th className="sar-num">GST on sale</th>
                <th className="sar-num">GST credit</th>
                <th className="sar-num">Net GST</th>
                <th className="sar-num">Margin inc GST</th>
                <th className="sar-num">Margin ex GST</th>
                <th>GST treatment</th>
                <th>Invoice</th>
                <th className="sar-num">Paid</th>
            <th className="sar-num">Outstanding (Due)</th>
            <th>Payment</th>
            <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.dealId}>
                  <td>{fmtDate(r.saleDate)}</td>
                  <td>{r.stockNumber || '-'}</td>
                  <td className="sar-vin">{r.vin || '-'}</td>
                  <td>{r.vehicle}</td>
                  <td>
                    {r.customer || '-'}
                    {/* Winnie, Checks_20 Sep §8: the chase team has to see that the buyer or
                        the amount changed before they act on what is in front of them. It
                        persists until somebody acknowledges it - not until the next reload. */}
                    {r.flaggedAmended && (
                      <button
                        type="button"
                        className="sar-amended"
                        title={
                          (r.flaggedAmendedReason
                            ? 'Amended: ' + r.flaggedAmendedReason + '. '
                            : 'This sale was amended after the documents were issued. ') +
                          'Click to acknowledge.'
                        }
                        disabled={ackId === r.invoiceId}
                        onClick={() => void acknowledge(r.invoiceId)}
                      >
                        {ackId === r.invoiceId ? 'Acknowledging…' : 'Amended'}
                      </button>
                    )}
                  </td>
                  <td className="sar-num">{money(r.purchase)}</td>
                  <td className="sar-num">{money(r.sale)}</td>
                  <td className="sar-num">{money(r.gstOnSale)}</td>
                  <td className="sar-num">{money(r.gstCredit)}</td>
                  <td className="sar-num">{money(r.netGst)}</td>
                  <td className={r.marginIncGst < 0 ? 'sar-num sar-loss' : 'sar-num'}>
                    {money(r.marginIncGst)}
                  </td>
                  <td className={r.marginExGst < 0 ? 'sar-num sar-loss' : 'sar-num'}>
                    {money(r.marginExGst)}
                  </td>
                  <td>
                    {r.treatmentLabel}                    {r.basis === 'projected' && (
                      <span
                        className="sar-flag sar-flag-projected"
                        title="No sales invoice yet, so this GST is a forecast. The GST Register will not release the credit until the sale is invoiced."
                      >
                        projected
                      </span>
                    )}
                    {r.capped && (
                      <span
                        className="sar-flag"
                        title="Sold at or below cost, so the notional credit is capped at the GST on the sale."
                      >
                        capped
                      </span>
                    )}
                  </td>
                  <td>
                    {r.invoiceNumber || <span className="sar-muted">Not invoiced</span>}
                  </td>
                  <td className="sar-num">
                    <PaidCell row={r} onSaved={refresh} />
                  </td>
                  <td className="sar-num">
                    {r.outstanding === null ? (
                      <span className="sar-muted">-</span>
                    ) : (
                      money(r.outstanding)
                    )}
                  </td>
                  {/*
                    Payment status and deal status are two different facts, and the
                    spec treats them that way: a car can be an active sale and part
                    paid at the same time. The chase list keys off this column, not
                    the one beside it.
                  */}
                  <td>
                    <span
                      className={'sar-pay sar-pay-' + r.paymentStatus}
                      title={
                        r.onChase
                          ? 'Still owed, so still on the AR chase list.'
                          : 'Paid in full. Off the AR chase list.'
                      }
                    >
                      {AR_PAYMENT_STATUS_LABEL[r.paymentStatus]}
                    </span>
                    {r.lastPaymentDate && (
                      <em className="sar-paid-on">last {r.lastPaymentDate}</em>
                    )}
                  </td>
                  <td>
                    {r.status === 'settled' ? (
                      <span
                        className="sar-settled"
                        title="Paid in full. Off the AR reminders list."
                      >
                        ✓ Settled
                      </span>
                    ) : r.status === 'cancelled' ? (
                      <span className="sar-muted">Cancelled</span>
                    ) : (
                      <span className="sar-active">Active</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            {totals && (
              <tfoot>
                <tr>
                  <td colSpan={5}>
                    {totals.count} {totals.count === 1 ? 'sale' : 'sales'} in{' '}
                    {report?.periodLabel}
                  </td>
                  <td className="sar-num">{money(totals.purchase)}</td>
                  <td className="sar-num">{money(totals.sale)}</td>
                  <td className="sar-num">{money(totals.gstOnSale)}</td>
                  <td className="sar-num">{money(totals.gstCredit)}</td>
                  <td className="sar-num">{money(totals.netGst)}</td>
                  <td className="sar-num">{money(totals.marginIncGst)}</td>
                  <td className="sar-num">{money(totals.marginExGst)}</td>
                  <td colSpan={2}>{totals.invoiced} invoiced</td>
                  <td className="sar-num">{money(totals.received)}</td>
                  <td className="sar-num">{money(totals.outstanding)}</td>
                  {/* Payment, then Status: two cells, to match the head. */}
                  <td />
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      <p className="sar-note">
        Outstanding is shown only where a sales invoice exists in the platform. A vehicle
        sold and settled before it was invoiced here reads "Not invoiced" rather than
        showing its full sale price as a debt.
        {report && report.missingPrice > 0 && (
          <>
            {' '}
            {report.missingPrice}{' '}
            {report.missingPrice === 1 ? 'sold deal has' : 'sold deals have'} no sale price
            recorded and {report.missingPrice === 1 ? 'is' : 'are'} not listed.
          </>
        )}
      </p>
    </div>
  )
}
